import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Eye,
  MoreHorizontal,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  Calendar,
  User,
  Mail,
  Phone,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserDetailsModal from "./UserDetailsModal";

const SubscriptionTableRow = ({ subscription, isOpen, onToggle, getSubscriptionStatus }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const actionButtonRef = useRef(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });

  // Get status for this subscription
  const getStatus = () => {
    if (getSubscriptionStatus) {
      return getSubscriptionStatus(subscription);
    }
    
    // Fallback logic
    if (subscription.plan_name === 'Free Trial' || subscription.plan_name === 'Trial Plan') {
      return 'trial';
    }
    
    if (subscription.marked_as_paid === '1' || subscription.marked_as_paid === true || subscription.marked_as_paid === 1) {
      return 'paid';
    }
    
    if (subscription.marked_as_paid === '0' || subscription.marked_as_paid === false || subscription.marked_as_paid === 0) {
      return 'unpaid';
    }
    
    if (subscription.status) {
      const status = subscription.status.toLowerCase();
      if (status.includes('paid') || status === 'active' || status === 'completed' || status === 'success') {
        return 'paid';
      }
      if (status.includes('unpaid') || status === 'pending' || status === 'due') {
        return 'unpaid';
      }
      if (status.includes('failed') || status === 'cancelled' || status === 'expired' || status === 'inactive') {
        return 'failed';
      }
    }
    
    return 'unpaid'; // Default
  };

  const subscriptionStatus = getStatus();

  useEffect(() => {
    if (isOpen && actionButtonRef.current) {
      const buttonRect = actionButtonRef.current.getBoundingClientRect();
      const tableContainer = actionButtonRef.current.closest(".table-container");
      const containerRect = tableContainer
        ? tableContainer.getBoundingClientRect()
        : null;

      let right = 0;
      if (containerRect) {
        right = containerRect.right - buttonRect.right;
      }

      setDropdownPosition({
        top: buttonRect.bottom + 8,
        right: right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownClick =
        dropdownRef.current && dropdownRef.current.contains(event.target);
      const isActionButtonClick =
        actionButtonRef.current &&
        actionButtonRef.current.contains(event.target);

      if (!isDropdownClick && !isActionButtonClick) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "GH₵0";
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return {
          label: "Paid",
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
          iconColor: "text-green-600"
        };
      case 'unpaid':
        return {
          label: "Unpaid",
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: Clock,
          iconColor: "text-yellow-600"
        };
      case 'trial':
        return {
          label: "Free Trial",
          color: "bg-purple-100 text-purple-800 border-purple-200",
          icon: Clock,
          iconColor: "text-purple-600"
        };
      case 'failed':
        return {
          label: "Failed",
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
          iconColor: "text-red-600"
        };
      default:
        return {
          label: status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown",
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
          iconColor: "text-gray-600"
        };
    }
  };

  const getPlanBadge = (planName) => {
    const planColors = {
      "Free Trial": "bg-gray-100 text-gray-800 border-gray-200",
      "Trial Plan": "bg-gray-100 text-gray-800 border-gray-200",
      Bronze: "bg-amber-100 text-amber-800 border-amber-200",
      Silver: "bg-slate-100 text-slate-800 border-slate-200",
      Gold: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Platinum: "bg-blue-100 text-blue-800 border-blue-200",
    };

    return planColors[planName] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const statusBadge = getStatusBadge(subscriptionStatus);

  return (
    <>
      <motion.div
        whileHover={{ backgroundColor: "#fafafa" }}
        className="grid grid-cols-[minmax(280px,2.5fr)_minmax(120px,1.5fr)_minmax(120px,1.5fr)_minmax(100px,1.2fr)_minmax(110px,1.3fr)_minmax(120px,1.5fr)_minmax(60px,0.7fr)] gap-3 px-4 py-4 transition-colors duration-200 min-w-[1100px] lg:px-6"
      >
        {/* Customer */}
        <div className="min-w-0">
          <div className="flex gap-3 items-center">
            <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="flex gap-2 items-center text-sm font-medium text-gray-900 truncate">
                <Mail className="flex-shrink-0 w-3 h-3" />
                <span className="truncate">
                  {subscription.email || subscription.user?.email || "N/A"}
                </span>
              </p>
              <div className="flex gap-2 items-center text-xs text-gray-500">
                <Phone className="flex-shrink-0 w-3 h-3" />
                <span className="truncate">
                  {subscription.phone_number || subscription.user?.phone_number || "N/A"}
                </span>
              </div>
              {subscription.user?.full_name && (
                <p className="text-xs text-gray-600 mt-1 truncate">
                  {subscription.user.full_name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Plan */}
        <div className="min-w-0">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPlanBadge(
              subscription.plan_name
            )} max-w-full truncate`}
          >
            {subscription.plan_name || "N/A"}
          </span>
        </div>

        {/* Package */}
        <div className="min-w-0">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPlanBadge(
              subscription.package_name
            )} max-w-full truncate`}
          >
            {subscription.package_name || "N/A"}
          </span>
        </div>

        {/* Amount */}
        <div className="min-w-0">
          <div className="flex items-center">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {formatCurrency(parseFloat(subscription.amount) || 0)}
            </span>
          </div>
        </div>

        {/* Status */}
        <div className="min-w-0">
          <div className="flex gap-2 items-center">
            <span
              className={`inline-flex gap-1.5 items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadge.color} max-w-full truncate`}
            >
              <statusBadge.icon className={`flex-shrink-0 w-3 h-3 ${statusBadge.iconColor}`} />
              <span className="truncate">{statusBadge.label}</span>
            </span>
          </div>
        </div>

        {/* Created Date */}
        <div className="min-w-0">
          <div className="flex gap-1 items-center">
            <Calendar className="flex-shrink-0 w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900 truncate">
              {formatDate(subscription.created_at)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end min-w-0">
          <div className="relative">
            <motion.button
              ref={actionButtonRef}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
            >
              <MoreHorizontal className="w-4 h-4" />
            </motion.button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  className="fixed z-[9999] w-48 bg-white rounded-lg border border-gray-200 shadow-xl"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    right: `${dropdownPosition.right}px`,
                  }}
                  ref={dropdownRef}
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setShowUserModal(true);
                        onToggle();
                      }}
                      className="flex gap-3 items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <Eye className="w-4 h-4" />
                      View User Details
                    </button>

                    <button
                      onClick={() => {
                        navigate(`/payment-history`, {
                          state: {
                            userId: subscription.user_slug || subscription.user?.user_slug,
                          },
                        });
                        onToggle();
                      }}
                      className="flex gap-3 items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <DollarSign className="w-4 h-4" />
                      Payment History
                    </button>
                    
                    {/* Additional status-based actions can be added here */}
                    {subscriptionStatus === 'unpaid' && (
                      <button
                        onClick={() => {
                          // Add mark as paid functionality
                          //console.log('Mark as paid:', subscription.id);
                          onToggle();
                        }}
                        className="flex gap-3 items-center px-4 py-2 w-full text-sm text-green-700 transition-colors hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark as Paid
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        userId={subscription.user_slug || subscription.user?.user_slug}
      />
    </>
  );
};

export default SubscriptionTableRow;